import { Modal, CircularProgress } from "@mui/material";
import classNames from "classnames";
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CloseIcon } from "../common/Icons";
import styles from "./TwoFactorPopup.module.scss";
import eyehide from "../../login/img/eye-open.svg";
import eyeshow from "../../login/img/eye-show.svg";
import {
  useGetTwoFactorStatusQuery,
  useEnableTwoFactorMutation,
  useConfirmTwoFactorMutation,
  useDisableTwoFactorMutation,
} from "../../app/services/two-factor";

interface ITwoFactorPopup {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}

type Step = "status" | "enablePassword" | "scan" | "disablePassword" | "done";

const readApiError = (err: any, fallback: string) =>
  err?.data?.message || err?.error || fallback;

const TwoFactorPopup: FC<ITwoFactorPopup> = ({ show, setShow }) => {
  const [step, setStep] = useState<Step>("status");
  const [password, setPassword] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [otpauthUrl, setOtpauthUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const [secretCopied, setSecretCopied] = useState<boolean>(false);

  // Only ask the server while the dialog is actually open.
  const { data: status, isFetching: isLoadingStatus } =
    useGetTwoFactorStatusQuery(undefined, { skip: !show });
  const [enableTwoFactor, { isLoading: isEnabling }] = useEnableTwoFactorMutation();
  const [confirmTwoFactor, { isLoading: isConfirming }] = useConfirmTwoFactorMutation();
  const [disableTwoFactor, { isLoading: isDisabling }] = useDisableTwoFactorMutation();

  // The password and the pending secret are the two things worth not keeping
  // around: wipe them as soon as the dialog closes.
  const resetState = () => {
    setStep("status");
    setPassword("");
    setCode("");
    setSecret("");
    setOtpauthUrl("");
    setError("");
    setNotice("");
    setSecretCopied(false);
  };

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show]);

  const closePopup = () => setShow(false);

  const switchEyeHandler = (event: any) => {
    const input = event.target.previousElementSibling;
    const isHidden = input.getAttribute("type") === "password";
    input.setAttribute("type", isHidden ? "text" : "password");
    event.target.setAttribute("src", isHidden ? eyehide : eyeshow);
  };

  const startEnable = () => {
    setError("");
    setNotice("");
    setPassword("");
    setStep("enablePassword");
  };

  const submitEnable = (event: React.FormEvent) => {
    event.preventDefault();
    if (!password) {
      setError("Your password is required.");
      return;
    }
    setError("");
    enableTwoFactor({ password })
      .unwrap()
      .then((res) => {
        setSecret(res?.secret);
        setOtpauthUrl(res?.otpauth_url);
        setPassword("");
        setCode("");
        setStep("scan");
      })
      .catch((err) => {
        // 409 = already enabled elsewhere; refresh by dropping back to status.
        if (err?.status === 409) {
          setStep("status");
          setError(
            readApiError(err, "Two-factor authentication is already enabled.")
          );
          return;
        }
        setError(readApiError(err, "That password was not correct."));
      });
  };

  const submitConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setError("");
    confirmTwoFactor({ code })
      .unwrap()
      .then(() => {
        setSecret("");
        setOtpauthUrl("");
        setCode("");
        setNotice("Two-factor authentication is now on.");
        setStep("done");
      })
      .catch((err) => {
        setError(
          readApiError(err, "That code was not valid. Please try again.")
        );
      });
  };

  const startDisable = () => {
    setError("");
    setNotice("");
    setPassword("");
    setStep("disablePassword");
  };

  const submitDisable = (event: React.FormEvent) => {
    event.preventDefault();
    if (!password) {
      setError("Your password is required.");
      return;
    }
    setError("");
    disableTwoFactor({ password })
      .unwrap()
      .then(() => {
        setPassword("");
        setNotice("Two-factor authentication is now off.");
        setStep("done");
      })
      .catch((err) => {
        setError(readApiError(err, "That password was not correct."));
      });
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(secret).then(
      () => {
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      },
      () => setError("Could not copy — please select the key manually.")
    );
  };

  const codeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const labelClass =
    "pb-2 font-poppins font-medium lg:text-15 text-xs text-black2";
  const inputClass =
    "focus:outline-none w-full rounded-4 font-poppins font-normal lg:text-sm text-xs text-graydark py-14 px-3 border border-vorpgraylight";
  const primaryBtnClass = classNames(
    styles.customConfirmBtn,
    "flex items-center px-3 py-2 rounded-[6px] font-poppins font-semibold lg:text-sm text-xs text-[#fff] bg-vorpblue hover:text-vorpblue hover:bg-vorpmyanmar mr-2"
  );
  const cancelBtnClass = classNames(
    styles.cancelBtn,
    "flex items-center px-4 py-2 rounded-[6px] font-poppins font-semibold lg:text-sm text-xs text-black2 bg-offwhite hover:bg-anncolor"
  );

  const passwordField = (
    <div className="relative">
      <input
        autoComplete="current-password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter your password"
        className={inputClass}
      />
      <img
        src={eyeshow}
        alt="eye-open"
        className={classNames(
          styles.theeye,
          "absolute top-1/3 right-4 cursor-pointer w-[22px] h-[16px] object-cover"
        )}
        onClick={switchEyeHandler}
      />
    </div>
  );

  const renderStatus = () => {
    if (isLoadingStatus) {
      return (
        <div className="flex justify-center py-6">
          <CircularProgress size={28} />
        </div>
      );
    }
    const isEnabled = status?.two_factor_enabled;
    const isPending = status?.two_factor_pending;
    return (
      <div>
        <p className="font-poppins lg:text-15 text-xs text-black2">
          Status:{" "}
          <span
            className={classNames(
              "font-semibold",
              isEnabled ? "text-vorpgreen" : "text-redPink"
            )}
          >
            {isEnabled ? "Enabled" : "Not enabled"}
          </span>
        </p>
        {isPending && !isEnabled ? (
          <p className="mt-2 font-poppins text-12 text-graydark">
            You started setting this up but never entered a code. Start again to
            get a fresh QR code.
          </p>
        ) : (
          <p className="mt-2 font-poppins text-12 text-graydark">
            {isEnabled
              ? "You will be asked for a code from your authenticator app each time you sign in."
              : "Add a code from an authenticator app on top of your password when signing in."}
          </p>
        )}
        <div className="flex justify-start mt-9">
          {isEnabled ? (
            <button onClick={startDisable} className={primaryBtnClass}>
              Turn off
            </button>
          ) : (
            <button onClick={startEnable} className={primaryBtnClass}>
              {isPending ? "Start again" : "Set up"}
            </button>
          )}
          <button onClick={closePopup} className={cancelBtnClass}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const renderEnablePassword = () => (
    <form onSubmit={submitEnable}>
      <p className={labelClass}>Confirm your password</p>
      {passwordField}
      <p className="mt-2 font-poppins text-12 text-graydark">
        We ask again before turning on two-factor authentication.
      </p>
      <div className="flex justify-start mt-9">
        <button
          type="submit"
          disabled={isEnabling}
          className={classNames(
            primaryBtnClass,
            isEnabling ? "opacity-50 pointer-events-none" : ""
          )}
        >
          Continue
        </button>
        <button
          type="button"
          onClick={() => setStep("status")}
          className={cancelBtnClass}
        >
          Back
        </button>
      </div>
    </form>
  );

  const renderScan = () => (
    <form onSubmit={submitConfirm}>
      <p className={labelClass}>1. Scan this with your authenticator app</p>
      <div className="flex justify-center py-4">
        {otpauthUrl ? (
          <div className="p-3 bg-white border border-vorpgraylight rounded-lg">
            {/* Rendered in the browser so the secret is never sent to a
                third-party QR service. */}
            <QRCodeSVG value={otpauthUrl} size={168} level="M" />
          </div>
        ) : null}
      </div>
      <p className="font-poppins text-12 text-graydark text-center">
        Can't scan it? Enter this key manually:
      </p>
      <div className="flex items-center justify-center mt-2 mb-6">
        <code className="font-mono text-xs text-black2 break-all bg-offwhite rounded px-3 py-2">
          {secret}
        </code>
        <button
          type="button"
          onClick={copySecret}
          className="ml-2 font-poppins text-12 text-vorpblue whitespace-nowrap"
        >
          {secretCopied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className={labelClass}>2. Enter the 6-digit code it shows</p>
      <input
        type="text"
        value={code}
        onChange={codeChangeHandler}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="123456"
        className={classNames(inputClass, "tracking-[0.4em]")}
      />
      <div className="flex justify-start mt-9">
        <button
          type="submit"
          disabled={isConfirming || code.length !== 6}
          className={classNames(
            primaryBtnClass,
            isConfirming || code.length !== 6
              ? "opacity-50 pointer-events-none"
              : ""
          )}
        >
          Turn on
        </button>
        <button type="button" onClick={closePopup} className={cancelBtnClass}>
          Cancel
        </button>
      </div>
    </form>
  );

  const renderDisablePassword = () => (
    <form onSubmit={submitDisable}>
      <p className={labelClass}>Confirm your password</p>
      {passwordField}
      <p className="mt-2 font-poppins text-12 text-graydark">
        Turning this off removes the authenticator code from your sign in.
      </p>
      <div className="flex justify-start mt-9">
        <button
          type="submit"
          disabled={isDisabling}
          className={classNames(
            primaryBtnClass,
            isDisabling ? "opacity-50 pointer-events-none" : ""
          )}
        >
          Turn off
        </button>
        <button
          type="button"
          onClick={() => setStep("status")}
          className={cancelBtnClass}
        >
          Back
        </button>
      </div>
    </form>
  );

  const renderDone = () => (
    <div>
      <p className="font-poppins lg:text-15 text-xs text-black2">{notice}</p>
      <div className="flex justify-start mt-9">
        <button onClick={closePopup} className={primaryBtnClass}>
          Done
        </button>
      </div>
    </div>
  );

  const renderStep = () => {
    if (step === "enablePassword") return renderEnablePassword();
    if (step === "scan") return renderScan();
    if (step === "disablePassword") return renderDisablePassword();
    if (step === "done") return renderDone();
    return renderStatus();
  };

  return (
    <div>
      <Modal open={show} onClose={closePopup} disableEnforceFocus={true}>
        <div>
          <div className={classNames("z-10 fixed top-0 left-0 w-full h-full")}>
            <div
              className={classNames(
                "relative z-10 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 md:max-w-[457px] 2xs:max-w-[400px] 5xs:max-w-[340px] max-w-[300px] bg-[#fff] rounded-xl"
              )}
            >
              <div className="flex justify-between items-center bg-darkblue px-10 py-6 rounded-tl-xl rounded-tr-xl">
                <p className="text-left font-poppins font-semibold lg:text-lg text-base text-[#fff]">
                  Two-Factor Authentication
                </p>
                <span
                  onClick={closePopup}
                  className="text-[#B5B5C3] text-20 cursor-pointer"
                >
                  <CloseIcon />
                </span>
              </div>
              <div className="md:px-10 px-6 pt-9 pb-10">
                {renderStep()}
                <p
                  className={classNames(
                    error ? "" : "hidden",
                    "mt-4 font-poppins text-12 text-redPink"
                  )}
                >
                  {error}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TwoFactorPopup;
